import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import {
    CreatePublicShareLinkApi,
    type CreatePublicShareLinkApi as CreatePublicShareLinkApiType,
} from '@telecom/shared/infrastructure/api-functions/create-public-share-link-api';
import {
    PublicShareApi,
    type PublicShareApi as PublicShareApiType,
    type ShareAccessScope,
    type ShareInvitationBatchResponse,
    type ShareInvitationResultResponse,
} from '@telecom/shared/infrastructure/api-functions/public-share-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { ShareLinkService } from '@/services/share-page/share-link-service';
import type { ReportShareInvitationCode } from '@/services/share-page/report-share-attribution';
import type {
    ReportShareConfiguration,
    ReportShareService,
} from '../report-share-service';

const INVITATION_CODE_BY_REPORT_ROUTE: Partial<Record<string, ReportShareInvitationCode>> = {
    'novelty-check-report': 'ip_searching_novelty_search',
    'fto-pro': 'ip_searching_fto',
};

/** 报告定向分享服务实现。 */
@injectable()
export class ReportShareServiceImpl implements ReportShareService {
    constructor(
        @inject(CreatePublicShareLinkApi)
        private readonly _createPublicShareLinkApi: CreatePublicShareLinkApiType,
        @inject(PublicShareApi)
        private readonly _publicShareApi: PublicShareApiType,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(ShareLinkService)
        private readonly _shareLinkService: ShareLinkService,
    ) {}

    async initialize(): Promise<ReportShareConfiguration> {
        const created = await this._createPublicShareLinkApi.createPublicShareLink({
            body: {
                task_id: this._currentTaskService.taskId,
            },
        });
        const initialized = await this._publicShareApi.getShareInit({
            share_id: created.share_id,
        });
        const members = initialized.members ?? [];
        const invitationCode = INVITATION_CODE_BY_REPORT_ROUTE[this._currentTaskService.taskType];
        let shareUrl: string;
        if (invitationCode) {
            const owner = members.find(member => member.member_type === 'OWNER');
            if (!owner?.member_id) {
                throw new Error('Missing report share owner');
            }
            shareUrl = this._shareLinkService.buildShareUrl(created.share_id, {
                mediaType: 'link',
                inviterId: owner.member_id,
                invitationCode,
            });
        } else {
            shareUrl = this._shareLinkService.buildShareUrl(created.share_id);
        }
        return {
            shareId: created.share_id,
            shareUrl,
            accessScope: initialized.access_scope ?? created.access_scope,
            members,
        };
    }

    invite(shareId: string, emails: string[]): Promise<ShareInvitationBatchResponse> {
        const normalizedEmails = Array.from(new Set(
            emails
                .map(email => email.trim().toLowerCase())
                .filter(Boolean),
        ));
        return this._publicShareApi.createInvitations({
            share_id: shareId,
            emails: normalizedEmails,
        });
    }

    resend(shareId: string, invitationId: string): Promise<ShareInvitationResultResponse> {
        return this._publicShareApi.resendInvitation({
            share_id: shareId,
            invitation_id: invitationId,
        });
    }

    async remove(shareId: string, invitationId: string): Promise<void> {
        const removed = await this._publicShareApi.removeInvitation({
            share_id: shareId,
            invitation_id: invitationId,
        });
        if (!removed) {
            throw new Error('Failed to remove invitation');
        }
    }

    async updateAccessScope(shareId: string, accessScope: ShareAccessScope): Promise<ShareAccessScope> {
        const response = await this._publicShareApi.updateAccessScope({
            share_id: shareId,
            access_scope: accessScope,
        });
        return response.access_scope;
    }
}
